import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, ClipboardPaste, KeyRound, QrCode, ScanLine, Server, ShieldX, Smartphone, XCircle } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { clientsApi } from '@/api/clients';
import { clientDetailApi } from '@/api/client-detail';
import { useAuth } from '@/auth';
import {
  licensingApi,
  type RegistrationRequestRecord,
  type ResolveInstallationResponse,
  type ResolveRegistrationRequestResponse,
} from '@/api/licensing';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { Header } from '@/components/Header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  getLicensingRequestErrorView,
  getRegistrationRequestApprovalViewState,
  getResolveResultState,
  validateInstallationPayload,
  type LicensingRequestErrorView,
} from './licensingViewModel';
import { isMailArchiverApp, MailArchiverIcon } from './MailArchiverIcon';
import { LicensingE2EEntitlementPanel } from './LicensingE2EEntitlementPanel';
import { MonitoringEnrollmentFlow } from './MonitoringEnrollmentFlow';

type ResolvedRegistrationApproval = {
  result: ResolveRegistrationRequestResponse;
  locator: string;
};

function formatDateTime(value?: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU');
}

export function LicensingPage() {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const fixedClientId = Number(searchParams.get('clientId') || 0) || null;
  const fixedClientName =
    typeof location.state === 'object' && location.state && 'clientName' in location.state
      ? String((location.state as { clientName?: unknown }).clientName || '')
      : '';
  const fixedClient = useQuery({
    queryKey: ['client-detail', fixedClientId],
    queryFn: () => clientDetailApi.getMobileDetail(fixedClientId!),
    enabled: !!fixedClientId,
  });
  const resolvedFixedClientName = fixedClient.data?.client?.name || fixedClientName;
  const resolvedFixedClientLabel = clientTargetLabel(fixedClientId, resolvedFixedClientName);
  const clientScopedBackTo = fixedClientId ? `/visits/client/${fixedClientId}?tab=licensing&licensingSection=licenses` : undefined;
  const [scannerOpen, setScannerOpen] = useState(false);
  const [payload, setPayload] = useState('');
  const [lastResult, setLastResult] = useState<ResolveInstallationResponse | null>(null);
  const [lastRegistration, setLastRegistration] = useState<ResolvedRegistrationApproval | null>(null);
  const [invalidRequest, setInvalidRequest] = useState<LicensingRequestErrorView | null>(null);

  const resolve = useMutation({
    mutationFn: (value: string) => licensingApi.resolveInstallation(value.trim()),
    onSuccess: (result) => {
      setPayload('');
      setInvalidRequest(null);
      setLastResult(result);
      setLastRegistration(null);
      if (result.status === 'registered') {
        toast.success('Установка найдена');
      } else if (result.status === 'unregistered') {
        toast.warning('Установка не зарегистрирована');
      } else {
        toast.error('Нет доступа к клиенту');
      }
    },
    onError: (error) => {
      setLastResult(null);
      setLastRegistration(null);
      const view = getLicensingRequestErrorView(error, 'installation');
      setInvalidRequest(view);
      toast.error(view.title);
    },
  });
  const resolveRegistration = useMutation({
    mutationFn: (value: string) =>
      licensingApi.resolveRegistrationRequest(
        value.trim(),
        fixedClientId ?? undefined,
      ),
    onSuccess: (result, locator) => {
      setPayload('');
      setInvalidRequest(null);
      setLastResult(null);
      setLastRegistration({ result, locator: locator.trim() });
      toast.success('QR регистрации найден');
    },
    onError: (error) => {
      setLastResult(null);
      setLastRegistration(null);
      const view = getLicensingRequestErrorView(error, 'registration_request');
      setInvalidRequest(view);
      toast.error(view.title);
    },
  });

  const submitPayload = (value = payload) => {
    const validation = validateInstallationPayload(value);
    if (!validation.ok) {
      setLastResult(null);
      setLastRegistration(null);
      setInvalidRequest({ title: 'QR не распознан', detail: validation.message });
      toast.error(validation.message);
      return;
    }
    if (validation.kind === 'registration_request') {
      resolveRegistration.mutate(validation.payload);
      return;
    }
    resolve.mutate(validation.payload);
  };

  const handleScan = (value: string) => {
    setScannerOpen(false);
    setPayload(value);
    submitPayload(value);
  };

  return (
    <div className="min-h-full bg-background">
      <Header title="Лицензии" showBack backTo={clientScopedBackTo} />

      <main className="p-4 space-y-4">
        {fixedClientId && hasRole('licensing-e2e') && (
          <LicensingE2EEntitlementPanel clientId={fixedClientId} />
        )}

        {fixedClientId && (
          <MonitoringEnrollmentFlow
            key={fixedClientId}
            clientId={fixedClientId}
            clientName={resolvedFixedClientName}
          />
        )}

        {!fixedClientId && <section className="rounded-lg border bg-card p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <QrCode className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-medium">{fixedClientId ? 'Привязка к клиенту' : 'Проверка установки'}</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {fixedClientId
                  ? `Сканируйте QR регистрации. Привязка будет только к ${resolvedFixedClientLabel}.`
                  : 'Сканируйте QR в приложении клиента или вставьте payload вручную.'}
              </p>
            </div>
          </div>

          <div className="mt-4 grid gap-2">
            <Button type="button" size="lg" className="w-full" onClick={() => setScannerOpen(true)}>
              <ScanLine className="h-5 w-5" />
              Сканировать QR
            </Button>
            <Textarea
              value={payload}
              onChange={(event) => setPayload(event.target.value)}
              placeholder="DSLI1:..."
              className="min-h-24 font-mono text-xs"
            />
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => submitPayload()}
              disabled={resolve.isPending || resolveRegistration.isPending}
            >
              <ClipboardPaste className="h-4 w-4" />
              Проверить вручную
            </Button>
          </div>
        </section>}

        {!fixedClientId && (resolve.isPending || resolveRegistration.isPending) && (
          <StateCard
            icon={<Smartphone className="h-5 w-5" />}
            title="Проверяю установку"
            tone="muted"
          />
        )}

        {!fixedClientId && invalidRequest && !resolve.isPending && (
          <StateCard
            icon={<XCircle className="h-5 w-5" />}
            title={invalidRequest.title}
            detail={invalidRequest.detail}
            tone="danger"
          />
        )}

        {!fixedClientId && lastResult && (
          <ResolveResult
            result={lastResult}
            fixedClientId={fixedClientId}
            fixedClientName={resolvedFixedClientName}
            onOpenClient={(clientId) => navigate(`/visits/client/${clientId}?tab=licensing&licensingSection=licenses`)}
            onOpenStation={(stationId) =>
              navigate(`/more/licensing/stations/${stationId}`, {
                state: { licensingBackTo: clientScopedBackTo ?? '/more/licensing' },
              })
            }
          />
        )}

        {!fixedClientId && lastRegistration && (
          <RegistrationRequestResult
            key={lastRegistration.result.request.id}
            result={lastRegistration.result}
            locator={lastRegistration.locator}
            fixedClientId={fixedClientId}
            fixedClientName={resolvedFixedClientName}
            onApproved={(request) => setLastRegistration({
              result: { ...lastRegistration.result, request },
              locator: '',
            })}
          />
        )}
      </main>

      {scannerOpen && <BarcodeScanner onScan={handleScan} onClose={() => setScannerOpen(false)} />}
    </div>
  );
}

function ResolveResult({
  result,
  fixedClientId,
  fixedClientName,
  onOpenClient,
  onOpenStation,
}: {
  result: ResolveInstallationResponse;
  fixedClientId: number | null;
  fixedClientName: string;
  onOpenClient: (clientId: number) => void;
  onOpenStation: (stationId: string) => void;
}) {
  const viewState = getResolveResultState(result);

  if (viewState.kind === 'registered' && result.station) {
    const station = result.station;
    const stationClientLabel =
      fixedClientId === station.clientId
        ? clientDisplayLabel(station.clientId, fixedClientName)
        : clientDisplayLabel(station.clientId);
    return (
      <section className="rounded-lg border bg-card p-4">
        <div className="flex items-start gap-3">
          <StatusIcon tone="success" icon={<CheckCircle2 className="h-5 w-5" />} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-medium">{viewState.title}</h2>
              <Badge variant={station.status === 'HEALTHY' ? 'visit' : 'outline'}>{station.status}</Badge>
            </div>
            <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              {isMailArchiverApp(station.appId) && <MailArchiverIcon className="h-4 w-4 [image-rendering:pixelated]" />}
              {station.appId} / {stationClientLabel}
            </p>
          </div>
        </div>

        <dl className="mt-4 grid gap-3 text-sm">
          <InfoRow label="Station ID" value={station.id} mono />
          <InfoRow label="Installation ID" value={station.installationId} mono />
          <InfoRow label="Hostname" value={station.hostname || '—'} />
          {station.policy?.notes && <InfoRow label="Комментарий" value={station.policy.notes} />}
          <InfoRow label="Версия" value={station.currentVersion || '—'} />
          <InfoRow label="Платформа" value={`${station.os}/${station.arch}`} />
          <InfoRow label="Последний контакт" value={formatDateTime(station.lastSeenAt)} />
          {station.decommissionedAt && <InfoRow label="Выведена" value={formatDateTime(station.decommissionedAt)} />}
        </dl>

        <div className="mt-4 grid gap-2">
          <Button type="button" className="w-full" onClick={() => onOpenStation(station.id)}>
            {isMailArchiverApp(station.appId) ? (
              <MailArchiverIcon className="h-4 w-4 [image-rendering:pixelated]" />
            ) : (
              <Server className="h-4 w-4" />
            )}
            Открыть станцию
          </Button>
          <Button type="button" variant="outline" className="w-full" onClick={() => onOpenClient(station.clientId)}>
            Открыть клиента
          </Button>
        </div>
      </section>
    );
  }

  if (viewState.kind === 'no_access') {
    return (
      <StateCard
        icon={<ShieldX className="h-5 w-5" />}
        title={viewState.title}
        detail="Установка зарегистрирована, но ds-main не подтвердил вашу видимость этого клиента."
        tone="danger"
      />
    );
  }

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <StatusIcon tone="warning" icon={<XCircle className="h-5 w-5" />} />
        <div className="min-w-0 flex-1">
          <h2 className="font-medium">{viewState.title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Для привязки этой установки нужен отдельный QR регистрации из приложения.
          </p>
        </div>
      </div>
      <dl className="mt-4 grid gap-3 text-sm">
        <InfoRow label="App" value={result.identity.appId} />
        <InfoRow label="Installation ID" value={result.identity.installationId} mono />
      </dl>
      <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
        <p className="font-medium">Нужен QR регистрации из приложения</p>
        <p className="mt-1">
          Этот QR только идентифицирует установку. Для привязки откройте MailArchiver на компьютере клиента,
          нажмите «Создать QR для регистрации» и отсканируйте новый QR здесь.
        </p>
      </div>
    </section>
  );
}

function RegistrationRequestResult({
  result,
  locator,
  fixedClientId,
  fixedClientName,
  onApproved,
}: {
  result: ResolveRegistrationRequestResponse;
  locator: string;
  fixedClientId: number | null;
  fixedClientName: string;
  onApproved: (request: RegistrationRequestRecord) => void;
}) {
  const [clientId, setClientId] = useState(fixedClientId ? String(fixedClientId) : '');
  const [stationNote, setStationNote] = useState(result.request.stationNote ?? '');
  const [confirmedContextKey, setConfirmedContextKey] = useState<string | null>(null);
  const clients = useQuery({
    queryKey: ['licensing', 'clients-for-registration-approval'],
    queryFn: () => clientsApi.getForSelector(),
    enabled: !fixedClientId,
  });
  const approve = useMutation({
    mutationFn: () => {
      const targetClientId = fixedClientId || Number(clientId);
      const normalizedStationNote = stationNote.trim() || undefined;
      if (!approvalView.canApprove || !locator) {
        throw new Error('Подтвердите точный запрос, приложение и клиента');
      }
      const confirmation = {
        locator,
        confirmedRequestId: result.request.id,
        confirmedAppId: result.request.appId,
        confirmedClientId: targetClientId,
      };
      if (fixedClientId) {
        return licensingApi.approveClientRegistrationRequest(
          result.request.id,
          targetClientId,
          confirmation,
          normalizedStationNote,
        );
      }
      return licensingApi.approveRegistrationRequest(
        result.request.id,
        targetClientId,
        confirmation,
        normalizedStationNote,
      );
    },
    onSuccess: (request) => {
      onApproved(request);
      toast.success('Регистрация подтверждена');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Не удалось подтвердить регистрацию');
    },
  });

  const request = result.request;
  const approvalView = getRegistrationRequestApprovalViewState({
    request,
    requestId: request.id,
    appId: request.appId,
    fixedClientId,
    selectedClientId: clientId,
    confirmedContextKey,
  });
  const selectedClientName = fixedClientId
    ? fixedClientName
    : (clients.data ?? []).find((client) => client.id === approvalView.targetClientId)?.name ?? '';
  const exactClientLabel = approvalView.targetClientId
    ? clientDisplayLabel(approvalView.targetClientId, selectedClientName)
    : 'не выбран';

  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <StatusIcon tone={request.status === 'PENDING' ? 'warning' : 'success'} icon={<KeyRound className="h-5 w-5" />} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-medium">Запрос регистрации</h2>
            <Badge variant={request.status === 'PENDING' ? 'today' : 'outline'}>{request.status}</Badge>
          </div>
          <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            {isMailArchiverApp(request.appId) && <MailArchiverIcon className="h-4 w-4 [image-rendering:pixelated]" />}
            {request.appId} · {request.hostname || result.identity.hostname || 'без hostname'}
          </p>
        </div>
      </div>

      <dl className="mt-4 grid gap-3 text-sm">
        <InfoRow label="Request ID" value={request.id} mono />
        <InfoRow label="Application ID" value={request.appId} mono />
        <InfoRow label="Installation ID" value={request.installationId} mono />
        <InfoRow label="Версия" value={request.currentVersion || result.identity.softwareVersion || '—'} />
        <InfoRow label="Платформа" value={`${request.os}/${request.arch}`} />
        {request.stationNote && <InfoRow label="Комментарий" value={request.stationNote} />}
        <InfoRow label="Действует до" value={formatDateTime(request.expiresAt)} />
      </dl>

      {approvalView.showApprovalControls && (
        <div className="mt-4 grid gap-2">
          {approvalView.mode === 'client_scoped' ? (
            <div className="rounded-md bg-muted p-3 text-sm">
              Привязка будет выполнена к {clientTargetLabel(approvalView.targetClientId, fixedClientName)}.
            </div>
          ) : (
            <Select value={clientId} onValueChange={setClientId} disabled={clients.isLoading || approve.isPending}>
              <SelectTrigger>
                <SelectValue placeholder={clients.isLoading ? 'Загрузка клиентов...' : 'Выберите клиента'} />
              </SelectTrigger>
              <SelectContent>
                {(clients.data ?? []).map((client) => (
                  <SelectItem key={client.id} value={String(client.id)}>
                    {client.name} [{client.id}]
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <label className="grid gap-1 text-sm">
            <span className="font-medium">Комментарий к станции</span>
            <Textarea
              value={stationNote}
              onChange={(event) => setStationNote(event.target.value)}
              placeholder="бухгалтерия, ПК Ивановой, сервер mail"
              maxLength={2000}
              disabled={approve.isPending}
              className="min-h-20"
            />
          </label>
          {approvalView.targetClientId && (
            <div className="rounded-md border bg-muted/40 p-3 text-sm">
              <p className="font-medium">Точное назначение регистрации</p>
              <dl className="mt-2 grid gap-2">
                <InfoRow label="Application ID" value={request.appId} mono />
                <InfoRow label="Request ID" value={request.id} mono />
                <InfoRow label="Клиент" value={exactClientLabel} />
              </dl>
              <label className="mt-3 flex items-start gap-2">
                <Checkbox
                  aria-label="Подтвердить точное назначение регистрации"
                  checked={approvalView.isConfirmed}
                  onCheckedChange={(checked) => setConfirmedContextKey(
                    checked === true ? approvalView.confirmationKey : null,
                  )}
                  disabled={approve.isPending}
                />
                <span>
                  Подтверждаю привязку запроса <span className="font-mono text-xs">{request.id}</span>{' '}
                  приложения <span className="font-mono text-xs">{request.appId}</span> к клиенту {exactClientLabel}.
                </span>
              </label>
            </div>
          )}
          <Button type="button" onClick={() => approve.mutate()} disabled={!approvalView.canApprove || approve.isPending}>
            <CheckCircle2 className="h-4 w-4" />
            Подтвердить регистрацию
          </Button>
        </div>
      )}

      {request.status === 'APPROVED' && (
        <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-200">
          Подтверждено. Приложение завершит регистрацию после следующей автоматической проверки.
        </div>
      )}
    </section>
  );
}

function clientTargetLabel(clientId?: number | null, clientName?: string): string {
  const name = clientName?.trim();
  if (name) {
    return `клиенту «${name}»`;
  }
  if (clientId) {
    return `клиенту ${clientId}`;
  }
  return 'выбранному клиенту';
}

function clientDisplayLabel(clientId: number, clientName?: string): string {
  const name = clientName?.trim();
  if (name) {
    return `${name} [${clientId}]`;
  }
  return `клиент ${clientId}`;
}

function StateCard({ icon, title, detail, tone }: { icon: ReactNode; title: string; detail?: string; tone: 'muted' | 'danger' }) {
  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="flex items-start gap-3">
        <StatusIcon tone={tone} icon={icon} />
        <div>
          <h2 className="font-medium">{title}</h2>
          {detail && <p className="mt-1 text-sm text-muted-foreground">{detail}</p>}
        </div>
      </div>
    </section>
  );
}

function StatusIcon({ icon, tone }: { icon: ReactNode; tone: 'success' | 'warning' | 'danger' | 'muted' }) {
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
        tone === 'success' && 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
        tone === 'warning' && 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
        tone === 'danger' && 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
        tone === 'muted' && 'bg-muted text-muted-foreground'
      )}
    >
      {icon}
    </div>
  );
}

function InfoRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid gap-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn('break-words', mono && 'font-mono text-xs')}>{value}</dd>
    </div>
  );
}
